import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart3, FileCog, Plus, Search, ShieldCheck } from 'lucide-react';
import { Skeleton } from '../../../shared/components/Skeleton';
import { Alert } from '../../../shared/components/Alert';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { Button } from '../../../shared/components/Button';
import { EmptyState } from '../../../shared/components/EmptyState';
import { Pagination } from '../../../shared/components/Pagination';
import { TextInput } from '../../../shared/components/TextInput';
import { usePermissions } from '../../../shared/hooks/usePermissions';
import { FEATURES } from '../../../shared/lib/permissions';

import { useContratos } from '../hooks';
import type { ContractSituacao, ListContractsParams } from '../api';
import { formatCurrencyBRL, formatDateBR } from '../utils/format';

const SITUACAO_OPTIONS: { value: ContractSituacao; label: string }[] = [
  { value: 'em_elaboracao', label: 'Em Elaboração' },
  { value: 'em_revisao_juridica', label: 'Em Revisão Jurídica' },
  { value: 'em_aprovacao', label: 'Em Aprovação' },
  { value: 'aguardando_assinatura', label: 'Aguardando Assinatura' },
  { value: 'assinado', label: 'Assinado' },
  { value: 'vigente', label: 'Vigente' },
  { value: 'suspenso', label: 'Suspenso' },
  { value: 'em_renovacao', label: 'Em Renovação' },
  { value: 'em_aditivo', label: 'Em Aditivo' },
  { value: 'encerrado', label: 'Encerrado' },
  { value: 'rescindido', label: 'Rescindido' },
  { value: 'cancelado', label: 'Cancelado' },
];

const SITUACAO_COLORS: Record<ContractSituacao, string> = {
  vigente: 'bg-green-100 text-green-800',
  suspenso: 'bg-yellow-100 text-yellow-800',
  em_aditivo: 'bg-yellow-100 text-yellow-800',
  encerrado: 'bg-red-100 text-red-800',
  rescindido: 'bg-red-100 text-red-800',
  cancelado: 'bg-red-100 text-red-800',
  em_elaboracao: 'bg-blue-100 text-blue-800',
  em_revisao_juridica: 'bg-blue-100 text-blue-800',
  em_aprovacao: 'bg-blue-100 text-blue-800',
  aguardando_assinatura: 'bg-blue-100 text-blue-800',
  assinado: 'bg-blue-100 text-blue-800',
  em_renovacao: 'bg-blue-100 text-blue-800',
};

function SituacaoBadge({ situacao }: { situacao: ContractSituacao }) {
  const label = SITUACAO_OPTIONS.find((o) => o.value === situacao)?.label ?? situacao;
  return (
    <span className={`inline-flex whitespace-nowrap items-center rounded-full px-2 py-0.5 text-xs font-medium ${SITUACAO_COLORS[situacao]}`}>
      {label}
    </span>
  );
}

const DEFAULT_PAGE_SIZE = 25;

export default function ContratosPage(): React.ReactElement {
  const navigate = useNavigate();
  const { can } = usePermissions();

  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [situacao, setSituacao] = useState<ContractSituacao | ''>('');
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const params: ListContractsParams = {
    search: debouncedSearch || undefined,
    situacao: situacao || undefined,
    page: Math.floor(offset / DEFAULT_PAGE_SIZE) + 1,
    limit: DEFAULT_PAGE_SIZE,
  };

  const { data, isLoading, isError, error } = useContratos(params);

  const items = data?.data ?? [];
  const paginationData = data?.pagination;

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Contratos Comerciais</h1>
          <p className="mt-1 text-sm text-gray-600">Ciclo de vida completo, da geração à assinatura e vigência.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {can(FEATURES.contratos, 'view') && (
            <Button variant="ghost" icon={<BarChart3 className="h-4 w-4" />} onClick={() => navigate('/contratos/indicadores')}>
              Indicadores
            </Button>
          )}
          {can(FEATURES.contratosModelos, 'view') && (
            <Button variant="ghost" icon={<FileCog className="h-4 w-4" />} onClick={() => navigate('/contratos/modelos')}>
              Modelos
            </Button>
          )}
          {can(FEATURES.contratosClausulas, 'view') && (
            <Button variant="ghost" icon={<ShieldCheck className="h-4 w-4" />} onClick={() => navigate('/contratos/clausulas')}>
              Cláusulas
            </Button>
          )}
          <Button icon={<Plus className="h-4 w-4" />} onClick={() => navigate('/contratos/novo')}>
            Novo Contrato
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <TextInput
          label="Buscar por código, número ou objeto"
          icon={<Search className="h-4 w-4" />}
          value={searchInput}
          onChange={(e) => { setSearchInput(e.target.value); setOffset(0); }}
        />
        <AutocompleteSelect
          label="Situação"
          options={[{ value: '', label: 'Todas as situações' }, ...SITUACAO_OPTIONS]}
          value={situacao}
          onChange={(v) => { setSituacao(v as ContractSituacao | ''); setOffset(0); }}
        />
      </div>

      {isError && (
        <Alert variant="error">{(error as Error)?.message ?? 'Erro ao carregar contratos.'}</Alert>
      )}

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          title="Nenhum contrato encontrado"
          description="Ajuste os filtros ou crie um novo contrato."
        />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Código</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Número</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Situação</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Vigência Final</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Valor Total</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Responsável</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {items.map((item) => (
                <tr
                  key={item.id}
                  onClick={() => navigate(`/contratos/${item.id}`)}
                  className="hover:bg-gray-50 cursor-pointer"
                >
                  <td className="px-4 py-3 font-mono text-xs text-gray-600">{item.codigo ?? '—'}</td>
                  <td className="px-4 py-3 font-medium text-gray-900">{item.numero}</td>
                  <td className="px-4 py-3"><SituacaoBadge situacao={item.situacao} /></td>
                  <td className="px-4 py-3 text-gray-600">{formatDateBR(item.vigenciaFinal)}</td>
                  <td className="px-4 py-3 text-gray-600">{formatCurrencyBRL(item.valorTotal)}</td>
                  <td className="px-4 py-3 text-gray-500 text-xs font-mono truncate max-w-[140px]">{item.responsavelComercialId}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {paginationData && paginationData.count > DEFAULT_PAGE_SIZE && (
        <Pagination
          offset={offset}
          limit={DEFAULT_PAGE_SIZE}
          count={paginationData.count}
          itemLabel="contrato"
          onChange={({ offset: o }) => setOffset(o)}
        />
      )}
    </div>
  );
}
